import {
  TRAVELLER_PLAN,
  TRAVELLER_AGE,
  TRAVELLER_BENEFIT,
  TRAVELLER_CLEAR_PLAN,
  TRAVELLER_CLEAR_BENEFIT,
  TRAVELLERS_PLAN_PARAMS,
  TravellerAge,
  TravellerPlanPayload,
  ClearPlan,
  TravellerParams,
  TRAVELLER_ELIGIBILITY,
  TRAVELLER_EXCLUSIONS,
  TRAVELLER_AMF_REQUIREMENTS,
  TRAVELLER_CLEAR_ALL_PLANS,
  TRAVELLER_PLAN_COVERAGE,
  TRAVELLER_SYNC_RATES,
  CLEAR_TRAVELLERS_PLAN_STATE,
  CLEAR_TRAVELLERS_PLAN_STATE_PAX
} from './types';
import { Dispatch } from 'redux';
import { ReduxStore } from '../payload-types';
import { find } from 'lodash';
import { dayjs } from '@trip-ux/common';

export const setTravellerPlanParam =
  (payload: TravellerParams) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: TRAVELLERS_PLAN_PARAMS,
      payload
    });
  };

// Refresh selected plan products' premium/tax/total from a freshly re-rated
// quote (retrieved-quote flow); leaves benefit products untouched.
export const syncPlanRatesFromQuote =
  (quote: ReduxStore['quote']) =>
  (dispatch: Dispatch): void => {
    dispatch({ type: TRAVELLER_SYNC_RATES, payload: quote });
  };

export const evaluateAge = (payload: TravellerAge) => {
  const ageCalcDate = payload.ageCalcDate ? dayjs(payload.ageCalcDate, payload.format) : dayjs();
  
  let dateOfBirth = payload.dateOfBirth;
  
  // Handle MUX DateOfBirth object format: { day, month, year, date }
  if (dateOfBirth && typeof dateOfBirth === 'object' && 'year' in (dateOfBirth as any)) {
    const { year, month, day } = dateOfBirth as any;
    if (year && month && day) {
      // Convert MUX format to string in the expected format
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      dateOfBirth = dayjs(dateStr, 'YYYY-MM-DD').format(payload.format);
    } else {
      // Incomplete date - treat as null
      dateOfBirth = null;
    }
  }
  
  // If no dateOfBirth but we have age, calculate dateOfBirth
  if (!dateOfBirth && payload.age) {
    dateOfBirth = dayjs().subtract(payload.age, 'years').format(payload.format);
  }

  const age = payload.age
    ? payload.age
    : dateOfBirth
    ? ageCalcDate.diff(dayjs(dateOfBirth, payload.format), 'years')
    : null;

  return { dateOfBirth, age };
};

export const setTravellerAge =
  (payload: TravellerAge) =>
  (dispatch: Dispatch): void => {
    const { dateOfBirth, age } = evaluateAge(payload);
    dispatch({
      type: TRAVELLER_AGE,
      payload: {
        paxId: payload.paxId,
        dateOfBirth,
        age
      }
    });
  };

export const setTravellerPlan =
  (payload: TravellerPlanPayload, dateFormat: string) =>
  (dispatch: Dispatch, getState: () => ReduxStore): void => {
    const { age } = find(getState().quoteForm.form.travellers, { referenceNumber: payload.paxId });
    dispatch({
      type: TRAVELLER_PLAN,
      payload: {
        ...payload,
        age,
        dateFormat
      }
    });
  };

export const setEligibility =
  (payload: boolean) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: TRAVELLER_ELIGIBILITY,
      payload
    });
  };

export const setExclusions =
  (payload: boolean) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: TRAVELLER_EXCLUSIONS,
      payload
    });
  };

export const setAmfRequirements =
  (payload: boolean) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: TRAVELLER_AMF_REQUIREMENTS,
      payload
    });
  };

export const setTravellerBenefit =
  (payload: TravellerPlanPayload, plan: string) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: TRAVELLER_BENEFIT,
      payload: {
        ...payload,
        plan
      }
    });
  };

export const setTravellerPlanCoverage =
  (payload: string) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: TRAVELLER_PLAN_COVERAGE,
      payload
    });
  };

export const clearTravellerPlan =
  (payload: ClearPlan) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: TRAVELLER_CLEAR_PLAN,
      payload
    });
  };

export const clearAllPlans =
  (payload: ClearPlan) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: TRAVELLER_CLEAR_ALL_PLANS,
      payload
    });
  };

export const clearTravellerBenefit =
  (payload: ClearPlan) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: TRAVELLER_CLEAR_BENEFIT,
      payload
    });
  };

export const clearTravellersPlanState =
  () =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: CLEAR_TRAVELLERS_PLAN_STATE
    });
  };

export const clearTravellersPlanStatePax =
  (paxIds: number[]) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: CLEAR_TRAVELLERS_PLAN_STATE_PAX,
      payload: { paxIds }
    });
  };
